import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ChevronLeft, RefreshCw, Play, CheckCircle2, RotateCcw, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

// ============================================================================
// Types — from CategoryRepairLoopService shapes
// ============================================================================

interface ProductTypeEntry {
  type_key: string;
  type_name?: string;
  [key: string]: unknown;
}

interface ReadinessSnapshot {
  productTypes: (ProductTypeEntry | string)[];
  productsWithoutType: number;
  totalProducts: number;
  avgAttrCount: string;
  minAttrCount: number;
  missingExtractionHints: string[];
  snapshotAt: string;
}

interface Patch {
  id: string;
  session_id: string;
  patch_type: string;
  risk_level: 'low' | 'medium' | 'high';
  status: string;
  summary: string | null;
  reason: string | null;
  diff: unknown;
  source_evidence: {
    pre_patch_snapshot?: ReadinessSnapshot;
    affected_kbe_ids?: string[];
    [key: string]: unknown;
  } | null;
  applied_at: string | null;
  rolled_back_at: string | null;
  created_at: string;
}

interface EvalPerProduct {
  kbeId: string;
  verdict: 'APPROVE' | 'HOLD' | 'REWORK' | 'ERROR' | 'UNKNOWN';
  criticalCount?: number;
  highCount?: number;
  issues?: Array<{ type: string; message: string }>;
  error?: string;
}

interface EvalSummary {
  patchId: string;
  approveCount: number;
  holdCount: number;
  reworkCount: number;
  errorCount?: number;
  perProduct: EvalPerProduct[];
}

interface Session {
  id: string;
  site_id: string;
  category_path: string;
  product_type: string | null;
  status: string;
  started_by: string | null;
  started_at: string;
  updated_at: string;
  canary_product_ids: string[] | null;
  current_patch_id: string | null;
  accepted_patch_id: string | null;
  last_eval_summary: EvalSummary | null;
  baseline_readiness_snapshot: ReadinessSnapshot | null;
  current_readiness_snapshot: ReadinessSnapshot | null;
}

interface SessionData {
  session: Session;
  patches: Patch[];
  evalSummary: EvalSummary | null;
  baseline: ReadinessSnapshot | null;
  current: ReadinessSnapshot | null;
}

// ============================================================================
// Helpers
// ============================================================================

const BASE = '/api/admin/category-repair';

const STATUS_META: Record<string, { label: string; cls: string }> = {
  diagnosing:              { label: 'Diagnosing',       cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  awaiting_patch_approval: { label: 'Awaiting patch',   cls: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  applying_patch:          { label: 'Applying patch',   cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  running_canary:          { label: 'Running canary',   cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  passed:                  { label: 'Passed',           cls: 'bg-green-100 text-green-800 border-green-300' },
  needs_next_patch:        { label: 'Needs next patch', cls: 'bg-orange-100 text-orange-800 border-orange-300' },
  needs_hitl:              { label: 'Needs review',     cls: 'bg-red-100 text-red-800 border-red-300' },
  rolled_back:             { label: 'Rolled back',      cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  failed:                  { label: 'Failed',           cls: 'bg-red-100 text-red-800 border-red-300' },
};

const RISK_META = {
  low:    { label: 'Low risk',    cls: 'bg-green-100 text-green-800 border-green-300' },
  medium: { label: 'Medium risk', cls: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  high:   { label: 'High risk',   cls: 'bg-red-100 text-red-800 border-red-300' },
};

const VERDICT_META: Record<EvalPerProduct['verdict'], { icon: string; cls: string }> = {
  APPROVE: { icon: '✓', cls: 'text-green-700 font-bold' },
  HOLD:    { icon: '⏸', cls: 'text-yellow-700 font-bold' },
  REWORK:  { icon: '↺', cls: 'text-orange-700 font-bold' },
  ERROR:   { icon: '✗', cls: 'text-red-700 font-bold' },
  UNKNOWN: { icon: '?', cls: 'text-slate-500' },
};

function StatusBadge({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? { label: status, cls: 'bg-slate-100 text-slate-700 border-slate-300' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function RiskBadge({ risk }: { risk: 'low' | 'medium' | 'high' }) {
  const meta = RISK_META[risk] ?? RISK_META.medium;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function PatchTypePill({ type }: { type: string }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
      {type.replace(/_/g, ' ')}
    </span>
  );
}

// ============================================================================
// Readiness snapshot card
// ============================================================================

function SnapshotCard({ title, snap }: { title: string; snap: ReadinessSnapshot }) {
  return (
    <div className="rounded-md border bg-muted/30 p-4 space-y-2 text-sm">
      <p className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{title}</p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
        <span className="text-muted-foreground">Products</span>
        <span className="tabular-nums font-medium">{snap.totalProducts.toLocaleString()}</span>
        <span className="text-muted-foreground">Avg attrs</span>
        <span className="tabular-nums font-medium">{snap.avgAttrCount}</span>
        <span className="text-muted-foreground">Min attrs</span>
        <span className="tabular-nums font-medium">{snap.minAttrCount}</span>
        <span className="text-muted-foreground">Without type</span>
        <span className={`tabular-nums font-medium ${snap.productsWithoutType > 0 ? 'text-orange-600' : ''}`}>
          {snap.productsWithoutType}
        </span>
        <span className="text-muted-foreground">Known types</span>
        <span className="font-medium">
          {snap.productTypes.length > 0
            ? snap.productTypes.map(pt =>
                typeof pt === 'string' ? pt : (pt.type_name ?? pt.type_key)
              ).join(', ')
            : '—'}
        </span>
      </div>
      {snap.missingExtractionHints.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground mt-2 mb-1">Missing extraction hints:</p>
          <div className="flex flex-wrap gap-1">
            {snap.missingExtractionHints.map(h => (
              <span key={h} className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-200">{h}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Patch card
// ============================================================================

function PatchCard({
  patch,
  session,
  onApply,
  onRunCanary,
  onAccept,
  onRollback,
  evalSummary,
  actioning,
}: {
  patch: Patch;
  session: Session;
  onApply: () => void;
  onRunCanary: () => void;
  onAccept: () => void;
  onRollback: () => void;
  evalSummary: EvalSummary | null;
  actioning: boolean;
}) {
  const [diffOpen, setDiffOpen] = useState(false);

  const canApply    = patch.status === 'proposed' && session.status === 'awaiting_patch_approval';
  const canRunCanary = patch.status === 'applied' && session.status === 'running_canary';
  const canAccept   = patch.status === 'applied' && session.status === 'passed';
  // fix_category_mapping and add_extraction_hints have side-effects the backend
  // cannot revert automatically — hide Rollback for these types to avoid a guaranteed failure.
  const NON_REVERTIBLE_TYPES = new Set(['fix_category_mapping', 'add_extraction_hints']);
  const isNonRevertible = NON_REVERTIBLE_TYPES.has(patch.patch_type);
  // Rollback requires source_evidence.pre_patch_snapshot which only exists after apply.
  // In multi-iteration sessions only the current (applied) or accepted patch can be rolled back —
  // rolling back an older applied patch would restore a stale snapshot and wipe newer changes.
  const hasRollbackEvidence = !!patch.source_evidence?.pre_patch_snapshot;
  const isCurrentOrAcceptedPatch =
    (patch.status === 'applied'  && (!session.current_patch_id  || session.current_patch_id  === patch.id)) ||
    (patch.status === 'accepted' && (!session.accepted_patch_id || session.accepted_patch_id === patch.id));
  const canRollback = !isNonRevertible &&
                      isCurrentOrAcceptedPatch &&
                      !['rolled_back'].includes(session.status) &&
                      hasRollbackEvidence;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1 flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <PatchTypePill type={patch.patch_type} />
              <RiskBadge risk={patch.risk_level} />
              <StatusBadge status={patch.status} />
            </div>
            {patch.summary && (
              <p className="text-sm font-medium mt-1">{patch.summary}</p>
            )}
          </div>
          <p className="text-xs text-muted-foreground shrink-0">
            {new Date(patch.created_at).toLocaleString()}
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {patch.reason && (
          <p className="text-sm text-muted-foreground">{patch.reason}</p>
        )}

        {/* Diff */}
        <div>
          <button
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setDiffOpen(v => !v)}
          >
            {diffOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {diffOpen ? 'Hide diff' : 'Show diff'}
          </button>
          {diffOpen && (
            <pre className="mt-2 text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
              {JSON.stringify(patch.diff, null, 2)}
            </pre>
          )}
        </div>

        {/* Evidence */}
        {patch.source_evidence && (
          <div className="text-xs text-muted-foreground space-y-1">
            {patch.source_evidence.affected_kbe_ids && (
              <p>{patch.source_evidence.affected_kbe_ids.length} products affected by mapping fix</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap pt-1">
          {canApply && (
            <Button size="sm" onClick={onApply} disabled={actioning} className="gap-1.5">
              <Play className="h-3.5 w-3.5" /> Apply patch
            </Button>
          )}
          {canRunCanary && (
            <Button size="sm" onClick={onRunCanary} disabled={actioning} className="gap-1.5">
              <Zap className="h-3.5 w-3.5" /> Run canary
            </Button>
          )}
          {canAccept && (
            <Button size="sm" onClick={onAccept} disabled={actioning} className="gap-1.5 bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> Accept patch
            </Button>
          )}
          {canRollback && (
            <Button size="sm" variant="outline" onClick={onRollback} disabled={actioning} className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50">
              <RotateCcw className="h-3.5 w-3.5" /> Rollback
            </Button>
          )}
          {isNonRevertible && ['applied', 'accepted'].includes(patch.status) && (
            <p className="text-xs text-muted-foreground">
              Rollback unavailable — {patch.patch_type.replace(/_/g, ' ')} has side-effects that require manual remediation.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Canary table
// ============================================================================

function CanaryTable({
  evalSummary,
  canaryIds,
}: {
  evalSummary: EvalSummary;
  canaryIds: string[];
}) {
  const { approveCount, holdCount, reworkCount, errorCount = 0, perProduct } = evalSummary;
  const total = perProduct.length;
  const allApproved = approveCount === total && total > 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 text-sm">
        <span className="font-medium">Canary result:</span>
        <span className="text-green-700 font-semibold">{approveCount} APPROVE</span>
        {holdCount > 0   && <span className="text-yellow-700 font-semibold">{holdCount} HOLD</span>}
        {reworkCount > 0 && <span className="text-orange-700 font-semibold">{reworkCount} REWORK</span>}
        {errorCount > 0  && <span className="text-red-700 font-semibold">{errorCount} ERROR</span>}
        {allApproved && <span className="ml-2 text-green-600 font-bold text-xs px-2 py-0.5 rounded-full bg-green-50 border border-green-200">All passed</span>}
      </div>

      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="text-left p-2 pl-3 text-xs font-medium text-muted-foreground">Product (KBE ID)</th>
              <th className="text-center p-2 text-xs font-medium text-muted-foreground">Verdict</th>
              <th className="text-right p-2 pr-3 text-xs font-medium text-muted-foreground">Issues</th>
            </tr>
          </thead>
          <tbody>
            {perProduct.map(p => {
              const vm = VERDICT_META[p.verdict] ?? VERDICT_META.UNKNOWN;
              return (
                <tr key={p.kbeId} className="border-b last:border-0">
                  <td className="p-2 pl-3 font-mono text-xs text-muted-foreground">{p.kbeId.slice(0, 8)}…</td>
                  <td className="p-2 text-center">
                    <span className={vm.cls}>{vm.icon} {p.verdict}</span>
                  </td>
                  <td className="p-2 pr-3 text-right tabular-nums">
                    {p.criticalCount || p.highCount ? (
                      <span className="text-xs text-red-700">
                        {p.criticalCount ? `${p.criticalCount} critical` : ''}{p.criticalCount && p.highCount ? ', ' : ''}{p.highCount ? `${p.highCount} high` : ''}
                      </span>
                    ) : p.error ? (
                      <span className="text-xs text-destructive">{p.error}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Main session page
// ============================================================================

export default function CategoryRepairSession() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate      = useNavigate();

  const [data, setData]         = useState<SessionData | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [actioning, setActioning] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadSession = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${BASE}/sessions/${sessionId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { loadSession(); }, [loadSession]);

  async function runAction(path: string) {
    setActioning(true);
    setActionError(null);
    try {
      const res = await authFetch(`${BASE}/sessions/${sessionId}${path}`, { method: 'POST' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      await loadSession();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setActioning(false);
    }
  }

  const session    = data?.session;
  const patches    = data?.patches ?? [];
  const evalSummary = data?.evalSummary ?? null;
  const activePatch = patches.find(p => ['proposed', 'applied'].includes(p.status)) ?? patches[patches.length - 1];

  const canDiagnose    = session && session.status === 'diagnosing';
  const canProposePatch = session && session.status === 'awaiting_patch_approval';
  const needsNextPatch  = session && session.status === 'needs_next_patch';

  return (
    <div className="p-6 space-y-6 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate('/catalog/repair')} className="gap-1">
          <ChevronLeft className="h-4 w-4" /> All sessions
        </Button>
        <span className="text-muted-foreground">/</span>
        {session ? (
          <>
            <span className="font-semibold truncate max-w-[320px]" title={session.category_path}>
              {session.category_path}
            </span>
            <StatusBadge status={session.status} />
          </>
        ) : (
          <Skeleton className="h-5 w-48" />
        )}
        <div className="ml-auto">
          <Button variant="outline" size="sm" onClick={loadSession} disabled={loading} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {actionError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      {loading && !data && (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-lg" />)}
        </div>
      )}

      {session && (
        <>
          {/* Session metadata */}
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <span>Started: <span className="text-foreground">{new Date(session.started_at).toLocaleString()}</span></span>
            {session.product_type && <span>Type: <span className="text-foreground">{session.product_type}</span></span>}
            <span>Site: <span className="text-foreground font-mono text-xs">{session.site_id.slice(0, 8)}…</span></span>
          </div>

          {/* Before/after snapshots */}
          {(data?.baseline || data?.current) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.baseline && <SnapshotCard title="Baseline" snap={data.baseline} />}
              {data.current  && <SnapshotCard title="Current"  snap={data.current} />}
            </div>
          )}

          {/* Primary action buttons */}
          <div className="flex items-center gap-3 flex-wrap">
            {canDiagnose && (
              <Button onClick={() => runAction('/diagnose')} disabled={actioning} className="gap-2">
                <Zap className="h-4 w-4" /> Run diagnosis
              </Button>
            )}
            {(canProposePatch || needsNextPatch) && (
              <Button onClick={() => runAction('/propose-patch')} disabled={actioning} className="gap-2">
                <Play className="h-4 w-4" /> Propose patch
              </Button>
            )}
          </div>

          {/* Canary results */}
          {evalSummary && session.canary_product_ids && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Canary results</CardTitle>
              </CardHeader>
              <CardContent>
                <CanaryTable evalSummary={evalSummary} canaryIds={session.canary_product_ids} />
                {/* Apply & Re-run when canary has failures + next patch available */}
                {(evalSummary.holdCount > 0 || evalSummary.reworkCount > 0) && needsNextPatch && (
                  <div className="mt-4 pt-4 border-t flex items-center gap-3">
                    <p className="text-sm text-muted-foreground flex-1">
                      Canary has HOLD/REWORK verdicts. Propose a follow-up patch to address them.
                    </p>
                    <Button onClick={() => runAction('/propose-patch')} disabled={actioning} className="gap-2">
                      <Play className="h-4 w-4" /> Propose follow-up patch
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Patches */}
          {patches.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Patches ({patches.length})
              </h2>
              {patches.map(patch => (
                <PatchCard
                  key={patch.id}
                  patch={patch}
                  session={session}
                  evalSummary={patch.id === evalSummary?.patchId ? evalSummary : null}
                  actioning={actioning}
                  onApply={() => runAction(`/patches/${patch.id}/apply`)}
                  onRunCanary={() => runAction(`/patches/${patch.id}/run-canary`)}
                  onAccept={() => runAction(`/patches/${patch.id}/accept`)}
                  onRollback={() => runAction(`/patches/${patch.id}/rollback`)}
                />
              ))}
            </div>
          )}

          {/* Passed state banner */}
          {session.status === 'passed' && (
            <Card className="border-green-300 bg-green-50">
              <CardContent className="py-6 text-center">
                <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="font-semibold text-green-800">Category repair passed.</p>
                <p className="text-sm text-green-700 mt-1">
                  All canary products were approved. Accept the patch to finalize.
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
